/**
 * @file        urgency-countdown.js
 * @description Live countdown timer for urgency messages with deadlines.
 *              Supports delivery, time_based, and promotion urgency types.
 *              Tiered display based on time remaining:
 *                > 24h  : day-level only  -> "X days"
 *                1-24h  : hours + minutes -> "Xh Ym" (re-rendered once a minute, no seconds clutter)
 *                < 1h   : minutes + seconds -> "Xm Ys" (live, ticks every second)
 *
 *              Listens for the native CustomEvent 'urgency:countdown-start'
 *              fired by urgency-renderer.js. Event detail shape:
 *              {
 *                deadline: number,      // epoch ms (deadline timestamp)
 *                urgencyType: string,   // 'delivery', 'time_based', or 'promotion'
 *                target: HTMLElement    // .urgency-message__timer span
 *              }
 *
 *              - Updates the target text every second with the tiered format above
 *              - Only ever one timer active; a new start event or a
 *                variant switch clears the previous interval.
 *              - On expiry: stops without painting a zeroed-out timer, and
 *                dispatches 'urgency:countdown-end' so the engine can
 *                re-evaluate and fall back to the fallback message.
 *
 * @listens     document -> urgency:countdown-start
 * @fires       document -> urgency:countdown-end
 */
(function () {
  'use strict';

  var TICK_MS = 1000;

  var state = {
    intervalId: null,
    target: null,
    deadline: 0,
    lastText: null,
  };

  function clear() {
    if (state.intervalId !== null) {
      clearInterval(state.intervalId);
    }
    state.intervalId = null;
    state.target = null;
    state.deadline = 0;
    state.lastText = null;
  }

  /**
   * Zero-pads a number to at least 2 digits.
   */
  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /**
   * Tiered countdown format:
   *   > 24h  : day-level only    -> "X days"
   *   1-24h  : hours + minutes   -> "Xh Ym"
   *   < 1h   : minutes + seconds -> "Xm Ys"
   * Only the two units relevant to the current tier are shown.
   */
  function format(msRemaining) {
    var totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    // > 24h: day-level only
    if (days > 0) {
      return days + (days === 1 ? ' day' : ' days');
    }

    // 1-24h: hours + minutes - always both, per MDX-3016 AC2 (e.g. "1h 00m").
    if (hours > 0) {
      return hours + 'h ' + pad(minutes) + 'm';
    }

    // < 1h: minutes + seconds - drop the minutes segment once it hits 0
    // rather than showing "0m 17s".
    if (minutes > 0) {
      return minutes + 'm ' + pad(seconds) + 's';
    }
    return seconds + 's';
  }

  function tick() {
    if (!state.target || !state.target.isConnected) {
      clear();
      return;
    }

    var remaining = state.deadline - Date.now();

    if (remaining <= 0) {
      var expiredTarget = state.target;
      clear();
      document.dispatchEvent(
        new CustomEvent('urgency:countdown-end', {
          bubbles: true,
          detail: { target: expiredTarget },
        }),
      );
      return;
    }

    var text = format(remaining);
    if (text !== state.lastText) {
      state.lastText = text;
      state.target.textContent = text;
    }
  }

  function start(detail) {
    if (!detail) return;

    // Support both 'delivery' and 'time_based' urgency types
    var supportedTypes = ['delivery', 'time_based', 'promotion'];
    if (!supportedTypes.includes(detail.urgencyType)) return;

    var deadline = Number(detail.deadline);
    if (!isFinite(deadline) || deadline <= 0) return;
    if (!detail.target) return;

    clear();

    state.target = detail.target;
    state.deadline = deadline;

    tick(); // paint immediately, don't wait a second
    if (state.intervalId === null && state.target) {
      state.intervalId = setInterval(tick, TICK_MS);
    }
  }

  document.addEventListener('urgency:countdown-start', function (event) {
    start(event.detail);
  });

  // Variant switch clears the slot and re-renders; if no new delivery
  // countdown starts, make sure the old interval is gone.
  document.addEventListener('urgency:countdown-clear', clear);

  // Safety: stop ticking while the tab is hidden, resume on return.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    if (state.intervalId !== null) tick();
  });
})();
